/**
 * NFT Manager
 * 
 * Creates, updates, and transfers game-related NFTs.
 * Integrates with Metaplex for Solana NFT operations.
 */
/* eslint-disable no-undef */

import { generateUUID } from '../utils/crypto';
import { PublicKey } from '@solana/web3.js';

// eslint-disable-next-line no-undef
if (typeof PublicKey === 'undefined') {
  // Fallback for environments where Solana web3.js is not available
  // eslint-disable-next-line no-global-assign
  PublicKey = class {
    constructor(address) {
      this.address = address;
    }
    toString() {
      return this.address;
    }
    static findProgramAddressSync() {
      return [{ toString: () => 'simulated-pda' }];
    }
  };
}

class NFTManager {
  constructor() {
    this.metaplex = null;
    this.connection = null;
    this.nfts = new Map(); // In-memory cache of NFTs
  }
  
  /**
   * Initialize the NFT manager
   * @param {Object} connection - Solana connection (optional)
   */
  async initialize(connection = null) {
    this.connection = connection;
    
    // Initialize Metaplex if connection is available
    if (connection) {
      try {
        // Dynamic import to avoid issues with SSR
        const { Metaplex } = await import('@metaplex-foundation/js');
        this.metaplex = new Metaplex(connection);
      } catch (error) {
        console.error('Failed to initialize Metaplex:', error);
        // Continue without Metaplex
      }
    }
    
    return this;
  }
  
  /**
   * Mint a new game card NFT
   * @param {string} gameId - Game ID
   * @param {string} playerAddress - Player's wallet address
   * @param {Object} metadata - NFT metadata
   * @returns {Promise<Object>} NFT data
   */
  async mintGameCard(gameId, playerAddress, metadata) {
    // Create NFT metadata
    const nftMetadata = {
      name: metadata.name || `Game Card #${Date.now()}`,
      symbol: metadata.symbol || 'PALMGAME',
      description: metadata.description || `PalmHeist Game Card for ${gameId}`,
      image: metadata.image || this._getDefaultImage(gameId),
      attributes: [
        { trait_type: 'Game ID', value: gameId },
        { trait_type: 'Card Type', value: metadata.cardType || 'Standard' },
        { trait_type: 'Created', value: new Date().toISOString() },
        ...(metadata.attributes || [])
      ],
      properties: {
        gameData: {
          gameId,
          revealed: false,
          result: null,
          ...(metadata.gameData || {})
        }
      }
    };
    
    // If Metaplex is available, mint on-chain
    if (this.metaplex) {
      try {
        const { nft } = await this.metaplex
          .nfts()
          .create({
            uri: await this._uploadMetadata(nftMetadata),
            name: nftMetadata.name,
            sellerFeeBasisPoints: 500, // 5%
            creators: [
              { address: playerAddress, share: 100 }
            ]
          });
        
        const nftData = {
          id: nft.address.toString(),
          mintAddress: nft.address.toString(),
          owner: playerAddress,
          metadata: nftMetadata,
          createdAt: Date.now()
        };
        
        // Store in cache
        this.nfts.set(nftData.id, nftData);
        
        return nftData;
      } catch (error) {
        console.error('Failed to mint NFT on-chain:', error);
        // Fall back to off-chain simulation
      }
    }
    
    // Off-chain simulation (for development or when Metaplex is unavailable)
    const nftId = generateUUID();
    
    const nftData = {
      id: nftId,
      mintAddress: nftId, // Use UUID as mint address for simulation
      owner: playerAddress,
      metadata: nftMetadata,
      createdAt: Date.now(),
      simulated: true // Flag to indicate this is a simulated NFT
    };
    
    // Store in cache
    this.nfts.set(nftId, nftData);
    
    // Persist NFT data
    await this._persistNft(nftData);
    
    return nftData;
  }
  
  /**
   * Update NFT metadata after game result is revealed
   * @param {string} mintAddress - NFT mint address
   * @param {Object} revealData - Reveal data
   * @returns {Promise<Object>} Updated NFT data
   */
  async revealCardMetadata(mintAddress, revealData) {
    // Get NFT from cache or storage
    const nft = await this._getNft(mintAddress);
    if (!nft) {
      throw new Error(`NFT not found: ${mintAddress}`);
    }
    
    // Update metadata
    const updatedMetadata = {
      ...nft.metadata,
      properties: {
        ...nft.metadata.properties,
        gameData: {
          ...nft.metadata.properties.gameData,
          revealed: true,
          result: revealData.result,
          revealedAt: new Date().toISOString()
        }
      }
    };
    
    // Update image if provided
    if (revealData.image) {
      updatedMetadata.image = revealData.image;
    }
    
    // Update attributes if provided
    if (revealData.attributes) {
      // Keep existing attributes that don't conflict
      const existingAttributes = nft.metadata.attributes.filter(
        attr => !revealData.attributes.some(newAttr => newAttr.trait_type === attr.trait_type)
      );
      
      updatedMetadata.attributes = [
        ...existingAttributes,
        ...revealData.attributes
      ];
    }
    
    // If Metaplex is available, update on-chain
    if (this.metaplex && !nft.simulated) {
      try {
        const uri = await this._uploadMetadata(updatedMetadata);
        
        await this.metaplex
          .nfts()
          .update({
            nftOrSft: { address: new PublicKey(mintAddress) },
            uri
          });
      } catch (error) {
        console.error('Failed to update NFT metadata on-chain:', error);
        // Continue with local update
      }
    }
    
    // Update in cache
    nft.metadata = updatedMetadata;
    this.nfts.set(mintAddress, nft);
    
    // Persist updated NFT
    await this._persistNft(nft);
    
    return nft;
  }
  
  /**
   * Transfer an NFT to a new owner
   * @param {string} mintAddress - NFT mint address
   * @param {string} fromAddress - Current owner's wallet address
   * @param {string} toAddress - New owner's wallet address
   * @returns {Promise<Object>} Updated NFT data
   */
  async transferNft(mintAddress, fromAddress, toAddress) {
    // Get NFT from cache or storage
    const nft = await this._getNft(mintAddress);
    if (!nft) {
      throw new Error(`NFT not found: ${mintAddress}`);
    }
    
    // Check ownership
    if (nft.owner !== fromAddress) {
      throw new Error(`NFT not owned by ${fromAddress}`);
    }
    
    // If Metaplex is available, transfer on-chain
    if (this.metaplex && !nft.simulated) {
      try {
        await this.metaplex
          .nfts()
          .transfer({
            nftOrSft: { address: new PublicKey(mintAddress) }, // eslint-disable-line no-undef
            fromOwner: new PublicKey(fromAddress), // eslint-disable-line no-undef
            toOwner: new PublicKey(toAddress) // eslint-disable-line no-undef
          });
      } catch (error) {
        console.error('Failed to transfer NFT on-chain:', error);
        // Continue with local update
      }
    }
    
    // Update ownership
    nft.owner = toAddress;
    
    // Update in cache
    this.nfts.set(mintAddress, nft);
    
    // Persist updated NFT
    await this._persistNft(nft);
    
    return nft;
  }
  
  /**
   * Get an NFT by mint address
   * @param {string} mintAddress - NFT mint address
   * @returns {Promise<Object>} NFT data
   */
  async getNft(mintAddress) {
    return this._getNft(mintAddress);
  }
  
  /**
   * Get all NFTs owned by a player
   * @param {string} playerAddress - Player's wallet address
   * @returns {Promise<Array<Object>>} Array of NFT data
   */
  async getPlayerNfts(playerAddress) {
    // If Metaplex is available, get NFTs from chain
    if (this.metaplex) {
      try {
        const nfts = await this.metaplex
          .nfts()
          .findAllByOwner({ owner: new PublicKey(playerAddress) }); // eslint-disable-line no-undef
        
        // Filter for PalmHeist game NFTs
        const gameNfts = nfts.filter(nft => {
          // Check if this is a PalmHeist game NFT
          // This would require a more sophisticated check in production
          return nft.symbol === 'PALMGAME';
        });
        
        // Convert to our format
        return Promise.all(
          gameNfts.map(async nft => {
            // Get metadata
            const metadata = await this._fetchMetadata(nft.uri);
            
            return {
              id: nft.address.toString(),
              mintAddress: nft.address.toString(),
              owner: playerAddress,
              metadata,
              createdAt: nft.createdAt || Date.now()
            };
          })
        );
      } catch (error) {
        console.error('Failed to get player NFTs from chain:', error);
        // Fall back to local cache
      }
    }
    
    // Get from cache
    return Array.from(this.nfts.values())
      .filter(nft => nft.owner === playerAddress);
  }
  
  // Private methods
  
  /**
   * Get default image for a game
   * @param {string} gameId - Game ID
   * @returns {string} Image URL
   * @private
   */
  _getDefaultImage(gameId) {
    // For now, return a placeholder
    return 'https://via.placeholder.com/500x500?text=PalmHeist+Game+Card';
  }
  
  /**
   * Upload metadata to IPFS or Arweave
   * @param {Object} metadata - Metadata to upload
   * @returns {Promise<string>} Metadata URI
   * @private
   */
  async _uploadMetadata(metadata) {
    // TODO: Implement actual upload to IPFS or Arweave
    // For now, return a mock URI
    return `https://arweave.net/mock-uri-${Date.now()}`;
  }
  
  /**
   * Fetch metadata from URI
   * @param {string} uri - Metadata URI
   * @returns {Promise<Object>} Metadata
   * @private
   */
  async _fetchMetadata(uri) {
    // TODO: Implement actual fetch from IPFS or Arweave
    // For now, return mock metadata
    return {
      name: 'Mock NFT',
      description: 'Mock NFT for development',
      image: 'https://via.placeholder.com/500x500?text=Mock+NFT',
      attributes: []
    };
  }
  
  /**
   * Get NFT from cache or storage
   * @param {string} mintAddress - NFT mint address
   * @returns {Promise<Object>} NFT data
   * @private
   */
  async _getNft(mintAddress) {
    // Check cache first
    if (this.nfts.has(mintAddress)) {
      return this.nfts.get(mintAddress);
    }
    
    // If Metaplex is available, try to get from chain
    if (this.metaplex && !mintAddress.includes('-')) { // Heuristic to check if it's a real mint address
      try {
        const nft = await this.metaplex
          .nfts()
          .findByMint({ mintAddress: new PublicKey(mintAddress) }); // eslint-disable-line no-undef
        
        // Get metadata
        const metadata = await this._fetchMetadata(nft.uri);
        
        const nftData = {
          id: nft.address.toString(),
          mintAddress: nft.address.toString(),
          owner: nft.ownerAddress.toString(),
          metadata,
          createdAt: nft.createdAt || Date.now()
        };
        
        // Store in cache
        this.nfts.set(mintAddress, nftData);
        
        return nftData;
      } catch (error) {
        console.error('Failed to get NFT from chain:', error);
        // Continue with local lookup
      }
    }
    
    // TODO: Try to load from persistent storage
    
    // Not found
    return null;
  }
  
  /**
   * Persist NFT data to storage
   * @param {Object} nft - NFT data
   * @returns {Promise<void>}
   * @private
   */
  async _persistNft(nft) {
    // TODO: Persist to storage
    // For now, just log
    console.log(`Persisting NFT: ${nft.id}`);
  }
}

export { NFTManager };
